import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import DealersListing, {
  IDealersListingRef,
} from '@molecules/DealersListing/DealersListing';
import StoreLocatorSearchBar from '@molecules/StoreLocatorSearchBar/StoreLocatorSearchBar';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import DealersMap, { IDealersMapRef } from './DealersMap';

interface Props {
  readonly autocompleteEndpoint: string;
  readonly countryCode: string;
  readonly defaultLat: string;
  readonly defaultLong: string;
  readonly directionsLabel: string;
  readonly distanceMeasurementUnitLabel: string;
  readonly distances: number[];
  readonly googleMapId: string;
  readonly googleMapsApiKey: string;
  readonly goToDealerLabel: string;
  readonly language: string;
  readonly noResultsTitleLabel: string;
  readonly noResultsSubtitleLabel: string;
  readonly resultLabel: string;
  readonly resultsLabel: string;
  readonly searchButtonLabel: string;
  readonly searchEndpoint: string;
  readonly searchPlaceholderLabel: string;
  readonly useMyCurrentLocationLabel: string;
  readonly viewOnMapLabel: string;
  readonly withinLabel: string;
}

const StoreLocator = ({
  autocompleteEndpoint,
  countryCode,
  defaultLat,
  defaultLong,
  directionsLabel,
  distanceMeasurementUnitLabel,
  distances,
  googleMapId,
  googleMapsApiKey,
  goToDealerLabel,
  language,
  noResultsTitleLabel,
  noResultsSubtitleLabel,
  resultLabel,
  resultsLabel,
  searchButtonLabel,
  searchEndpoint,
  searchPlaceholderLabel,
  useMyCurrentLocationLabel,
  viewOnMapLabel,
  withinLabel,
}: Props) => {
  const desktopDealersMapRef = useRef<IDealersMapRef>(null);
  const desktopDealersListingRef = useRef<IDealersListingRef>(null);
  const dealersContainerRef = useRef<HTMLDivElement>(null);
  const [dealers, setDealers] = useState<IStoreLocatorDealer[]>([]);
  const [selectedDealer, setSelectedDealer] =
    useState<IStoreLocatorDealer | null>(null);
  const [isListView, setIsListView] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isMobile = useMediaQuery(1024);
  const hasResults = dealers.length > 0;

  const scrollDealersContainerIntoView = () => {
    const dealersContainerPosition =
      dealersContainerRef.current?.getBoundingClientRect();
    if ((dealersContainerPosition?.top || 0) >= 0) return;

    dealersContainerRef.current?.scrollIntoView();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    scrollDealersContainerIntoView();
  }, [isListView, isMobile]);

  return (
    <section
      className="container mb-24 lg:mb-36"
      data-testid="CT-30-Store-Locator"
    >
      <div
        ref={dealersContainerRef}
        className={classNames('flex min-h-[500px] flex-col', {
          'lg:block lg:h-auto': hasResults,
        })}
      >
        <StoreLocatorSearchBar
          autocompleteEndpoint={autocompleteEndpoint}
          countryCode={countryCode}
          defaultLat={defaultLat}
          defaultLng={defaultLong}
          distanceMeasurementUnitLabel={distanceMeasurementUnitLabel}
          distances={distances}
          isInitialLoading={isInitialLoading}
          isListView={isListView}
          language={language}
          searchButtonLabel={searchButtonLabel}
          searchEndpoint={searchEndpoint}
          searchPlaceholderLabel={searchPlaceholderLabel}
          useMyCurrentLocationLabel={useMyCurrentLocationLabel}
          withinLabel={withinLabel}
          onSetIsListView={setIsListView}
          onSetIsInitialLoading={setIsInitialLoading}
          onSuccessfulSearch={(newDealers) => {
            setDealers(newDealers);
            desktopDealersMapRef.current?.closeActiveTooltip();
            desktopDealersListingRef.current?.scrollToTop();
            if (isMobile) scrollDealersContainerIntoView();
          }}
        />

        {isInitialLoading && !hasResults && (
          <LoadingSpinner extraClasses="min-h-[400px]" />
        )}

        {!isInitialLoading && !hasResults && (
          <motion.div
            key="noResults"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            className="flex grow items-center justify-center bg-white px-13 py-7.5"
            data-testid="CT-30-Store-Locator-No-Results-Section"
          >
            <div className="text-center text-black-100">
              <p
                className="h4 h4--bold mb-5"
                data-testid="CT-30-Store-Locator-No-Results-Title"
              >
                {noResultsTitleLabel}
              </p>
              <p
                className="body-four"
                data-testid="CT-30-Store-Locator-No-Results-Subtitle"
              >
                {noResultsSubtitleLabel}
              </p>
            </div>
          </motion.div>
        )}

        {!isInitialLoading && hasResults && (
          <motion.div
            key="withResults"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            className="relative w-full lg:flex lg:h-screen lg:gap-4"
            data-testid="CT-30-Store-Locator-Results-Section"
          >
            {isMobile && isListView && (
              <motion.div
                key="listView"
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
              >
                <DealersListing
                  dealers={dealers}
                  directionsLabel={directionsLabel}
                  goToDealerLabel={goToDealerLabel}
                  resultLabel={resultLabel}
                  resultsLabel={resultsLabel}
                  viewOnMapLabel={viewOnMapLabel}
                />
              </motion.div>
            )}
            {isMobile && !isListView && (
              <motion.div
                key="mapView"
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
              >
                <DealersMap
                  dealers={dealers}
                  googleMapId={googleMapId}
                  googleMapsApiKey={googleMapsApiKey}
                  goToDealerLabel={goToDealerLabel}
                />
              </motion.div>
            )}

            {!isMobile && (
              <>
                <DealersListing
                  ref={desktopDealersListingRef}
                  dealers={dealers}
                  directionsLabel={directionsLabel}
                  goToDealerLabel={goToDealerLabel}
                  resultLabel={resultLabel}
                  resultsLabel={resultsLabel}
                  selectedDealer={selectedDealer}
                  viewOnMapLabel={viewOnMapLabel}
                  onViewOnMapClick={(dealer: IStoreLocatorDealer) => {
                    setSelectedDealer(dealer);
                    desktopDealersMapRef.current?.selectDealerOnMap(dealer);
                  }}
                />
                <DealersMap
                  ref={desktopDealersMapRef}
                  dealers={dealers}
                  googleMapId={googleMapId}
                  googleMapsApiKey={googleMapsApiKey}
                  goToDealerLabel={goToDealerLabel}
                  onSetSelectedDealer={(dealer) => {
                    setSelectedDealer(dealer);

                    if (dealer)
                      desktopDealersListingRef.current?.scrollToDealer(dealer);
                  }}
                />
              </>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default StoreLocator;
